<template>
  <div class="collection">
    <div class="collection__top-wrapper">
      <div class="image-wrapper">
        <img
          :alt="info.name"
          :src="getImageFromWaxpeer(info.link_name || '', '500:500')"
          class="collection-card__image"
          height="50"
          loading="eager"
          width="50"
        >
      </div>
      <div class="collection__info">
        <h1 class="collection__info-title">
          {{ info.name }}
        </h1>
        <UiPageDescription class="collection__info-desc">
          {{ t('collection_desc', { name: info.name }) }}
        </UiPageDescription>
      </div>
    </div>

    <template v-if="items?.length && !loading">
      <template v-if="info.group === CollectionGroup.Agent">
        <h2 class="f fs gap8 mb24">
          <img
            :src="getImageFromProxy(`${siteHost}/icons/sides/counter-terrorist.png`, '50:50')"
            alt="terrorist"
            class="wh32"
            loading="lazy"
          >
          <span>{{ t('teams.counter-terrorists') }}</span>
        </h2>
        <div class="collection__grid">
          <PagesCollectionsCollectionCard
            v-for="item in agents['counter-terrorist']"
            :key="item?.market_hash_name"
            :item="item"
          />
        </div>
        <h2 class="f fs gap8 mb24 mt48">
          <img
            :src="getImageFromProxy(`${siteHost}/icons/sides/terrorist.png`, '50:50')"
            alt="terrorist"
            class="wh32"
            height="50"
            loading="lazy"
            width="50"
          >
          <span>{{ t('teams.terrorists') }}</span>
        </h2>
        <div class="collection__grid">
          <PagesCollectionsCollectionCard
            v-for="item in agents.terrorist"
            :key="item?.market_hash_name"
            :item="item"
          />
        </div>
      </template>
      <template v-else>
        <h2 class="f fs gap8 mb24">
          <svg
            class="light"
            height="24"
            width="24"
          >
            <use xlink:href="/icons/sprite.svg#i-soldier" />
          </svg>
          <span>{{ t(translates[info.group]) }}</span>
        </h2>
        <div class="collection__grid">
          <PagesCollectionsCollectionCard
            v-for="item in items"
            :key="item?.market_hash_name"
            :item="item"
          />
        </div>
      </template>

      <paginate-v3
        v-if="items?.length && !loading && pagesLength > 1"
        v-model="page"
        :pages-length="pagesLength"
        :total="totalItemsLength"
        :total-items="limit * page > pagesLength * limit ? pagesLength * limit : 50 * page"
      />
    </template>

    <LazyUiLoader
      v-else-if="loading"
      class="collections__loader"
    />

    <div v-else>
      <ui-no-result class="mxa" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AdvancedSearchBody, CollectionInfo } from '~/graphql/generated/schema-types';
import { CollectionGroup } from '~/graphql/generated/schema-types';
import { getImageFromWaxpeer } from '~/utils/get-image-url';

const props = defineProps<{
  items: AdvancedSearchBody[]
  info: CollectionInfo
  loading: boolean
  pagesLength: number
  totalItemsLength: number
  limit: number
}>();
const page = defineModel('page', {
  type: Number,
  default: 1,
});
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;
const { t } = useI18n();

const translates = Object.keys(CollectionGroup).reduce((acc, item) => {
  const key = CollectionGroup[item as keyof typeof CollectionGroup];
  let path = 'Weapon';
  if (key === CollectionGroup.Agent) {
    path = 'filters.weapon.Agents';
  } else if (key === CollectionGroup.Sticker) {
    path = 'filters.weapon.Stickers';
  } else if (key === CollectionGroup.Graffiti) {
    path = 'filters.weapon.Graffities';
  } else if (key === CollectionGroup.Patch) {
    path = 'filters.weapon.Patches';
  }

  return {
    ...acc,
    [key]: path,
  };
}, {} as Record<keyof typeof CollectionGroup, string>);

const agents = computed(() => {
  if (props.info.group !== CollectionGroup.Agent) {
    return {
      'counter-terrorist': [],
      'terrorist': [],
    };
  }
  return {
    'counter-terrorist': props.items.filter(i => i.team === 'counter-terrorists'),
    'terrorist': props.items.filter(i => i.team === 'terrorists'),
  };
});
</script>

<style lang="scss" scoped>
.collection {
  padding-top: 32px;
  padding-bottom: 80px;

  &__loader {
    margin: 120px auto;
  }

  &__top-wrapper {
    display: flex;
    align-items: center;
    gap: 56px;
    margin-bottom: 48px;

    .image-wrapper {
      width: 200px;
      height: 200px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: relative;
        z-index: 1;
      }

      &:after {
        content: '\e81f';
        font-family: 'fontello';
        position: absolute;
        inset: 0;
        text-align: center;
        display: block;
        font-size: 15rem;
        line-height: 20rem;
        color: var(--surfaces-outline);
        filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
        opacity: 0.2;
      }
    }
  }

  &__info {
    &-title {
      font-size: 28px;
      font-weight: 600;
      line-height: 36px;
      margin-bottom: 20px;
      color: var(--surfaces-on-surface);
    }

    &-desc {
      max-width: 600px;
      font-size: 16px;
      font-weight: 400;
      line-height: 32px;
      color: var(--surfaces-on-surface-variable);

      .btn-link {
        display: none;
        font-size: 16px;
        text-decoration: underline;
        color: var(--surfaces-on-surface);
        padding: 0 0 0 4px;
        position: relative;
        bottom: 2px;
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(201px, 1fr));
    gap: 16px;
  }

  @media (max-width: 768px) {
    &__top-filter {
      flex-wrap: wrap;
      gap: 8px;

      &:deep(.cselect) {
        margin-left: unset;
      }

      .filter-wrapper {
        width: 100%;
        overflow-x: auto;
      }
    }
  }
  @media (max-width: 525px) {
    padding-bottom: 16px;
    &__top-wrapper {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 24px;

      .image-wrapper {
        width: 230px;
        height: 230px;
      }
    }

    &__top-filter {
      &:deep(.cselect) {
        width: 100%;
      }
    }

    &__info {
      &-title {
        font-size: 24px;
        line-height: 32px;
        margin-bottom: 12px;
      }

      &-desc {
        font-size: 14px;
        font-weight: 400;
        line-height: 28px;

        .btn-link {
          display: inline-block;
          font-size: 14px;
        }
      }
    }

    &__grid {
      gap: 12px;
      grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    }
  }
}
</style>
